import { z } from 'zod';
import type { BaseParameterDetailAddition, BaseSqlDetailAddition } from '../types';
import { nullishAsUndefined, nullishToEmpty } from './utils';

export const sqlDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    aggregatorActivityName: nullishAsUndefined(z.string().optional()),
    activityName: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
    planId: nullishAsUndefined(z.number().optional()),
    sqlTextHashId: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ aggregatorActivityName, activityName, ...rest }) => ({
    ...rest,
    aggregatorActivityName: aggregatorActivityName ?? activityName,
  }))
  .pipe(
    z.object({
      instanceId: z.string().min(1, 'Instance ID'),
      aggregatorActivityName: z
        .string({ required_error: 'Aggregator Activity Name' })
        .min(1, 'Aggregator Activity Name'),
      fromTime: nullishAsUndefined(z.string().optional()),
      toTime: nullishAsUndefined(z.string().optional()),
      planId: nullishAsUndefined(z.number().optional()),
      sqlTextHashId: nullishAsUndefined(z.string().optional()),
    }),
  );

export const parameterDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    name: nullishAsUndefined(z.string().optional()),
    parameterName: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ name, parameterName, ...rest }) => ({
    ...rest,
    parameterName: name ?? parameterName,
  }))
  .pipe(
    z.object({
      parameterName: z.string({ required_error: 'Parameter Name' }).min(1, 'Parameter Name'),
      instanceId: z.string().min(1, 'Instance ID'),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export const documentTextSchema = z
  .object({
    aggregatorActivityName: nullishAsUndefined(z.string().optional()),
    activityName: nullishAsUndefined(z.string().optional()),
    instanceId: z.preprocess(
      nullishToEmpty(''),
      z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    ),
    sqlTextHashId: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ aggregatorActivityName, activityName, ...rest }) => ({
    ...rest,
    activityName: aggregatorActivityName ?? activityName,
  }))
  .pipe(
    z.object({
      activityName: z.preprocess(
        nullishToEmpty(''),
        z.string({ required_error: 'Activity Name' }).min(1, 'Activity Name'),
      ),
      instanceId: z.string().min(1, 'Instance ID'),
      sqlTextHashId: nullishAsUndefined(z.string().optional()),
    }),
  );

export type SqlDetailSchema = z.infer<typeof sqlDetailSchema>;
export type ParameterDetailSchema = z.infer<typeof parameterDetailSchema>;
export type DocumentTextSchema = z.infer<typeof documentTextSchema>;

export type SinglestoreSqlDetailAddition = BaseSqlDetailAddition & SqlDetailSchema;

export type SinglestoreParameterDetailAddition = Omit<
  BaseParameterDetailAddition & ParameterDetailSchema,
  'parameterName'
>;
